import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import React from "react";

import { getReconciliation, type ReconciliationLine } from "@/api/order_journal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtQty, lineTitle } from "@/lib/order-journal";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";

/**
 * Сверка заказа по строкам: заказано (1С) / собрано (последняя проверка со
 * сканами) / отгружено (сканы отгруженных паллет). Количества — в базовых
 * единицах. Единственная вкладка, которая ходит в 1С; её отказ — плашка с
 * текстом 1С, а не пустая таблица («расхождений нет» было бы ложью).
 */
export function ReconciliationTab({ orderRef }: { orderRef: string }): React.ReactElement {
  const q = useQuery({
    queryKey: ["order-journal-reconciliation", orderRef],
    queryFn: () => getReconciliation(orderRef),
    staleTime: 60_000,
  });

  if (q.isLoading) {
    return (
      <div className="flex justify-center py-10 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div
        role="alert"
        className="flex flex-col gap-2 rounded-lg border-2 border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 sm:flex-row sm:items-start"
      >
        <AlertTriangle className="size-5 shrink-0 text-rose-600" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold">Сверка не построена</div>
          <div className="break-words">{apiErrorText(q.error, "Не удалось получить сверку")}</div>
        </div>
        <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
          Повторить
        </Button>
      </div>
    );
  }
  const r = q.data!;
  const mismatches = r.lines.filter(
    (l) => l.collected_mismatch || l.shipped_mismatch || l.not_in_order,
  ).length;

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        «Собрано» — по проверке № {r.session_display_number ?? "—"}. Количества в базовых единицах.
        {mismatches > 0 ? ` Строк с расхождением: ${mismatches}.` : " Расхождений нет."}
      </p>
      {r.shipped_external_pallets > 0 && (
        <p className="rounded-md bg-amber-50 px-2 py-1 text-sm text-amber-900">
          Ещё {r.shipped_external_pallets} паллет отгружены без сборки в портале — их состав в
          «Отгружено» не вошёл.
        </p>
      )}
      {r.without_unit > 0 && (
        <p className="rounded-md bg-red-50 px-2 py-1 text-sm text-red-900">
          Паллет и сканов отгрузки без грузоместа: {r.without_unit} — их отгрузку не сопоставить,
          «Отгружено» может быть занижено.
        </p>
      )}
      <Card className="overflow-hidden">
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="px-2 py-2 font-medium">Товар</th>
              <th className="w-[4.5rem] px-2 py-2 text-right font-medium sm:w-24">Заказано</th>
              <th className="w-[4.5rem] px-2 py-2 text-right font-medium sm:w-24">Собрано</th>
              <th className="w-[4.5rem] px-2 py-2 text-right font-medium sm:w-24">Отгружено</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {r.lines.map((l) => (
              <Row key={`${l.nomenclature_ref}|${l.characteristic_ref}|${l.partia_ref}|${l.unit_coef}`} line={l} />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t font-semibold">
              <td className="px-2 py-2">Итого</td>
              <td className="px-2 py-2 text-right tabular-nums">{fmtQty(r.ordered_total)}</td>
              <td className="px-2 py-2 text-right tabular-nums">{fmtQty(r.collected_total)}</td>
              <td className="px-2 py-2 text-right tabular-nums">{fmtQty(r.shipped_total)}</td>
            </tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
}

function Row({ line }: { line: ReconciliationLine }): React.ReactElement {
  const bad = "bg-rose-50 font-semibold text-rose-800";
  return (
    <tr
      data-testid="recon-line"
      data-mismatch={line.collected_mismatch || line.shipped_mismatch || line.not_in_order ? "true" : undefined}
    >
      <td className="break-words px-2 py-2">
        {lineTitle(line)}
        {line.unit_coef !== 1 && (
          <span className="text-xs text-muted-foreground"> · единица ×{fmtQty(line.unit_coef)}</span>
        )}
        {line.not_in_order && (
          <span className="ml-1 rounded bg-rose-100 px-1 text-xs font-medium text-rose-800">
            нет в заказе
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-right tabular-nums">{fmtQty(line.ordered)}</td>
      <td className={cn("px-2 py-2 text-right tabular-nums", line.collected_mismatch && bad)}>
        {fmtQty(line.collected)}
      </td>
      <td className={cn("px-2 py-2 text-right tabular-nums", line.shipped_mismatch && bad)}>
        {fmtQty(line.shipped)}
      </td>
    </tr>
  );
}
